import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type DateTimeInput =
  | Date
  | string
  | number
  | dayjs.Dayjs
  | null
  | undefined;

/**
 * Formats a date/time value with a specific format pattern.
 * Returns the fallback (null unless one is given) if the input is null, undefined, or invalid.
 *
 * @param input - The date/time value to format
 * @param format - The format pattern (e.g., "DD MMM YYYY, h:mm a")
 * @param fallback - Returned instead when the input cannot be formatted. Pass the raw value to show
 *   something rather than nothing, or a placeholder like "Never"
 * @returns Formatted date/time string, or the fallback
 *
 * @example
 * formatDateWithPattern(new Date(), 'DD MMM YYYY, h:mm a') // "09 Oct 2025, 3:45 pm"
 * formatDateWithPattern(null, 'DD MMM YYYY') // null
 * formatDateWithPattern('not a date', 'DD MMM YYYY', 'not a date') // "not a date"
 * formatDateWithPattern(null, 'DD MMM YYYY', 'Never') // "Never"
 */
export function formatDateWithPattern<T = null>(
  input: DateTimeInput,
  format: string,
  fallback: T = null as T
): string | T {
  if (!input) {
    return fallback;
  }

  const date = dayjs(input);

  if (!date.isValid()) {
    return fallback;
  }

  return date.format(format);
}

export default formatDateWithPattern;
